import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Shirt, ImageIcon, ArrowRight, Info } from "lucide-react";
import { clsx } from "clsx";
import { customApi, STEP_META, STEP_ORDER, STATUS_LABEL, type CustomListingRow } from "../lib/customListing";

// Custom Listeler panosu: her custom urunun ilhamdan Etsy'ye hangi adimda oldugu.
export default function CustomListingsPage() {
  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ["custom-listings"],
    queryFn: () => customApi<CustomListingRow[]>("GET", ""),
  });

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900"><Shirt className="h-5 w-5 text-indigo-600" />Custom Listeler</h1>
        <p className="mt-1 text-sm text-gray-500">Kişiselleştirilebilir her ürün ilham listesinden Etsy'ye 7 adımda ilerler. Bir satıra tıklayıp kaldığın adımdan devam et.</p>
      </div>

      <div className="mb-4 flex items-start gap-2 rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-600 shadow-sm">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-600" />
        <div>
          Yeni bir custom liste başlatmak için <Link to="/analyses" className="font-medium text-indigo-600 hover:underline">Ürün Analizleri</Link>'nde
          custom etiketli bir analizi aç ve <span className="font-medium text-gray-900">Custom liste çalışması</span> düğmesine bas.
          Adımlar: {STEP_ORDER.map((k) => STEP_META[k].title).join(" → ")}.
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Yükleniyor…</div>
      ) : error ? (
        <p className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">{(error as Error).message}</p>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 py-16 text-center">
          <Shirt className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <p className="text-sm text-gray-500">Henüz custom liste yok.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2">Ürün</th>
                <th className="px-3 py-2">Adımlar</th>
                <th className="px-3 py-2">Sıradaki adım</th>
                <th className="px-3 py-2">Güncellendi</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <Link to={`/custom/${r.id}`} className="flex items-center gap-3">
                      <span className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
                        {r.analysis.imageUrls[0]
                          ? <img src={r.analysis.imageUrls[0]} alt="" loading="lazy" className="h-full w-full object-cover" />
                          : <ImageIcon className="m-3 h-6 w-6 text-gray-300" />}
                      </span>
                      <span className="min-w-0">
                        <span className="block font-medium text-gray-900 line-clamp-1">
                          {r.production && <span className={clsx("mr-1.5 rounded-full px-1.5 py-0.5 align-middle text-[10px] font-semibold", r.production === "ai" ? "bg-violet-100 text-violet-800" : "bg-emerald-100 text-emerald-800")} title={r.production === "ai" ? "Üretim yolu: AI (örnekler ve sipariş AI ile)" : "Üretim yolu: şablon (kodla basım)"}>{r.production === "ai" ? "AI" : "Şablon"}</span>}
                          {r.name}
                        </span>
                        <span className="text-xs text-gray-500">{r.analysis.productType}</span>
                      </span>
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      {r.steps.map((s) => (
                        <span key={s.key} title={`${STEP_META[s.key].n}. ${STEP_META[s.key].title}: ${STATUS_LABEL[s.status]}`}
                          className={clsx("h-2.5 w-6 rounded-full",
                            s.status === "done" ? "bg-emerald-500" : s.status === "legacy" ? "bg-amber-400" : s.status === "deferred" ? "bg-sky-300" : s.key === r.current ? "bg-indigo-400" : "bg-gray-200")} />
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-700">{STEP_META[r.current].n}. {STEP_META[r.current].title}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{new Date(r.updatedAt).toLocaleDateString("tr-TR")}</td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/custom/${r.id}`} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50">
                      Devam et <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
